import { useQuery, useReactiveVar } from '@apollo/client';
import { type PropsWithChildren as PWC, useCallback } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { Stack } from '@myphx-engineering/phx-design-system';

import { Loader } from '../../components/common/Loader';
import { PathwayAlertBanner } from '../../components/fill/PathwayAlertBanner';
import PatientMedicationDetails from '../../components/pages/order-details/PatientMedicationDetails';
import { ConditionalComponent } from '../../components/utility/ConditionalComponent';
import { getFragment } from '../../graphql/client/helpers/get-fragment';
import {
    CoverageStatus,
    type DigitallyReceivedPrescriberOrderInfoFragment,
    GetPrescriberOrderDocument,
} from '../../graphql/generated/graphql';
import { useAuthorizedMedication } from '../../hooks/use-order-medication';
import { usePathwayScreen } from '../../hooks/use-pathway-screen';
import {
    deduplicateOffers,
    filterOffersByPathway,
    filterOffersBySubPathway,
    usePrescriptionOffers,
} from '../../hooks/use-prescription-offers';
import { getAbsoluteRoute } from '../../util/get-absolute-route';

import {
    type CoverageCardData,
    FillPrescriptionContext,
    type FillPrescriptionCtx,
    coverageCardVar,
} from './shared';
import { useFillPathway } from './use-fill-prescription';

export const FillPrescriptionProvider = ({ children }: PWC) => {
    const { drxId } = useParams();
    const { pathway, subPathway } = useFillPathway();
    const pathwayScreen = usePathwayScreen();

    const cardsByDrxId = useReactiveVar(coverageCardVar);
    const coverageCard = drxId ? (cardsByDrxId[drxId] ?? null) : null;

    const setCoverageCard = useCallback(
        (data: CoverageCardData) => {
            if (drxId) {
                coverageCardVar({ ...coverageCardVar(), [drxId]: data });
            }
        },
        [drxId]
    );

    const {
        called,
        data,
        loading: orderLoading,
    } = useQuery(GetPrescriberOrderDocument, {
        variables: { prescriberOrderId: drxId! },
        skip: !drxId,
    });

    // Pre-fetch offers for this drx so we can use them for downstream screens
    const { offers: allOffers, loading: offersLoading } = usePrescriptionOffers(
        drxId,
        null
    );
    const pathwayOffers = filterOffersByPathway(allOffers, pathway);
    const subPathwayOffers = filterOffersBySubPathway(
        pathwayOffers,
        subPathway
    );
    const filteredOffers = deduplicateOffers(subPathwayOffers);

    const order = getFragment(data?.prescriberOrderV2);
    const medication = useAuthorizedMedication(order);

    if (!drxId) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    if (offersLoading || orderLoading || !called) {
        return <Loader />;
    }

    if (
        order?.__typename === 'DigitallyReceivedPrescriberOrder' &&
        !!order.assignmentTimestamp
    ) {
        return (
            <Navigate
                replace
                to={getAbsoluteRoute('order.prescriberOrderId.confirmation', {
                    prescriberOrderId: drxId,
                })}
            />
        );
    }

    if (
        !order ||
        // The medication doesn't exist if this is an unclaimed script, but is mandatory otherwise
        (!medication && order.__typename !== 'UnclaimedPrescriberOrder')
    ) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    // Caregiver screens are currently in the "legacy" version of this flow
    if (order.__typename === 'UnclaimedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.root', {
                    prescriberOrderId: drxId,
                })}
            />
        );
    }

    // @TODO: remove the non-null assertion and cast below when caregiver screens are added to this new flow
    const drx = order as DigitallyReceivedPrescriberOrderInfoFragment;

    const ctx: FillPrescriptionCtx = {
        medication: medication!,
        drx,
        drxId: drxId!,
        coverage: drx.coverageInfo?.coverageStatus ?? CoverageStatus.Unknown,
        coverageCard,
        setCoverageCard,
        allOffers,
        offers: filteredOffers,
    };

    const patient = getFragment(drx.patient);

    return (
        <FillPrescriptionContext.Provider value={ctx}>
            <Stack gap="lg">
                <ConditionalComponent
                    condition={
                        pathwayScreen !== 'on-hold' &&
                        pathwayScreen !== 'switch'
                    }
                >
                    <PathwayAlertBanner />
                    <PatientMedicationDetails
                        patientName={patient.fullName}
                        prescriberOrder={drx}
                    />
                </ConditionalComponent>
                {children}
            </Stack>
        </FillPrescriptionContext.Provider>
    );
};
